/**
 * Root component for the Monitor setup wizard.
 *
 * Owns the 5-step state machine and dispatches between step components.
 * All five steps render functional UI.
 *
 * Naming convention: "Monitor" (surface), "Sources" (wizard picker),
 * "Imports" (engineering term).
 */
import React, { useReducer } from 'react';
import { FiArrowLeft } from 'react-icons/fi';

import IconWrapper from '../../../ui/IconWrapper';
import {
  initialWizardState,
  wizardReducer,
  WIZARD_STEPS,
  WIZARD_STEP_LABELS,
  type WizardStep,
} from './types';
import SourceStep from './SourceStep';
import SourceConfigStep from './SourceConfigStep';
import DestinationStep from './DestinationStep';
import ScheduleStep from './ScheduleStep';
import ReviewStep from './ReviewStep';

interface MonitorSetupWizardProps {
  /** Returns the user to the Monitor surface (or wherever they came from). */
  onExit: () => void;
}

const stepIndex = (step: WizardStep): number => WIZARD_STEPS.indexOf(step);

const MonitorSetupWizard: React.FC<MonitorSetupWizardProps> = ({ onExit }) => {
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState);

  const currentIndex = stepIndex(state.step);

  return (
    // Two-part layout so the page scrolls reliably inside the app shell's
    // bounded, overflow-hidden <main>: an outer flex column that fills the
    // available height, and an inner `flex-1 min-h-0 overflow-y-auto` scroller.
    // Using flex-1 (not `h-full`) avoids the percentage-height resolution quirk
    // where a long step would overflow and clip instead of scroll.
    <div className="flex h-full flex-col overflow-hidden">
      <div className="mx-auto w-full max-w-3xl flex-1 min-h-0 space-y-6 overflow-y-auto px-4 pb-24 pt-8">
      <button
        type="button"
        onClick={onExit}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
      >
        <IconWrapper icon={FiArrowLeft} className="h-4 w-4" />
        Back to Monitor
      </button>

      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-slate-900">New monitor</h1>
        <p className="text-sm text-slate-600">
          Set up a recurring import from an external source into a Good
          Outcomes collection.
        </p>
      </header>

      <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 text-xs">
        {WIZARD_STEPS.map((step, i) => {
          const isCurrent = i === currentIndex;
          const isDone = i < currentIndex;
          return (
            <li key={step} className="flex items-center gap-2">
              <span
                aria-current={isCurrent ? 'step' : undefined}
                className={[
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-medium',
                  isDone
                    ? 'bg-emerald-100 text-emerald-700'
                    : isCurrent
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-500',
                ].join(' ')}
              >
                {i + 1}
              </span>
              <span
                className={[
                  'whitespace-nowrap',
                  isCurrent ? 'font-medium text-slate-900' : 'text-slate-500',
                ].join(' ')}
              >
                {WIZARD_STEP_LABELS[step]}
              </span>
              {i < WIZARD_STEPS.length - 1 ? (
                <span
                  className="mx-1 hidden h-px w-6 bg-slate-200 sm:inline-block"
                  aria-hidden="true"
                />
              ) : null}
            </li>
          );
        })}
      </ol>

      <section>
        {state.step === 'source' ? (
          <SourceStep
            onSelect={(skill, capability) =>
              dispatch({ type: 'select_source', skill, capability })
            }
          />
        ) : null}

        {state.step !== 'source' && state.selectedSkill ? (
          <p className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Source: <span className="font-medium text-slate-900">{state.selectedSkill.name || state.selectedSkill.skill_name}</span>
            {state.selectedCapability ? (
              <>
                {' '}
                · {state.selectedCapability.label}
              </>
            ) : null}
          </p>
        ) : null}

        {state.step === 'source_config' &&
        state.selectedSkill &&
        state.selectedCapability ? (
          <SourceConfigStep
            skill={state.selectedSkill}
            capability={state.selectedCapability}
            values={state.sourceConfig}
            onChange={(values) => dispatch({ type: 'update_source_config', values })}
            onBack={() => dispatch({ type: 'go_to', step: 'source' })}
            onContinue={() => dispatch({ type: 'go_to', step: 'destination' })}
          />
        ) : null}

        {state.step === 'destination' ? (
          <DestinationStep
            values={state.destinationConfig}
            onChange={(values) =>
              dispatch({ type: 'update_destination_config', values })
            }
            onBack={() => dispatch({ type: 'go_to', step: 'source_config' })}
            onContinue={() => dispatch({ type: 'go_to', step: 'schedule' })}
          />
        ) : null}

        {state.step === 'schedule' && state.selectedCapability ? (
          <ScheduleStep
            capability={state.selectedCapability}
            values={state.scheduleConfig}
            onChange={(values) =>
              dispatch({ type: 'update_schedule_config', values })
            }
            onBack={() => dispatch({ type: 'go_to', step: 'destination' })}
            onContinue={() => dispatch({ type: 'go_to', step: 'review' })}
          />
        ) : null}

        {state.step === 'review' &&
        state.selectedSkill &&
        state.selectedCapability ? (
          <ReviewStep
            skill={state.selectedSkill}
            capability={state.selectedCapability}
            monitorName={state.monitorName}
            onChangeMonitorName={(name) =>
              dispatch({ type: 'set_monitor_name', name })
            }
            onSaved={onExit}
            sourceConfig={state.sourceConfig}
            destinationConfig={state.destinationConfig}
            scheduleConfig={state.scheduleConfig}
            onBack={() => dispatch({ type: 'go_to', step: 'schedule' })}
          />
        ) : null}
      </section>
      </div>
    </div>
  );
};

export default MonitorSetupWizard;
